import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Save, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import {
  fetchAiPromptTemplate,
  createAiPromptTemplate,
  updateAiPromptTemplate,
  activateAiPromptTemplate,
} from "@/api/aiPromptTemplates";
import { fetchAiPersonas } from "@/api/aiPersonas";
import {
  AiPromptTemplate,
  AiPromptPurpose,
  AiPromptTemplateFormData,
  AI_PROMPT_PURPOSES,
} from "@/types/aiPromptTemplate";
import { AiPersona } from "@/types/aiPersona";

const AiPromptTemplateForm = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState<AiPromptTemplate | null>(null);
  const [personas, setPersonas] = useState<AiPersona[]>([]);

  // Form state
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState<AiPromptPurpose>("system");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [temperature, setTemperature] = useState<string>("");
  const [maxTokens, setMaxTokens] = useState<string>("");
  const [model, setModel] = useState("");
  const [personaId, setPersonaId] = useState<string>("none");

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load personas for the dropdown
      const personaData = await fetchAiPersonas();
      setPersonas(personaData);

      if (isEditing) {
        const data = await fetchAiPromptTemplate(Number(id));
        if (!data) {
          toast({ title: "Not Found", description: "Template not found.", variant: "destructive" });
          navigate("/admin/ai/prompt-templates");
          return;
        }
        setTemplate(data);
        setName(data.name);
        setPurpose(data.purpose);
        setDescription(data.description || "");
        setContent(data.content);
        setTemperature(data.metadata.temperature?.toString() || "");
        setMaxTokens(data.metadata.max_tokens?.toString() || "");
        setModel(data.metadata.model?.toString() || "");
        setPersonaId(data.personaId?.toString() || "none");
      } else {
        // Check for personaId in URL params (e.g. from persona detail page)
        const urlPersonaId = searchParams.get("personaId");
        if (urlPersonaId) {
          setPersonaId(urlPersonaId);
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast({ title: "Error", description: "Failed to load data.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const buildFormData = (): AiPromptTemplateFormData => ({
    purpose,
    name: name.trim(),
    content: content.trim(),
    description: description.trim() || undefined,
    personaId: personaId !== "none" ? parseInt(personaId, 10) : null,
    metadata: {
      ...(temperature ? { temperature: parseFloat(temperature) } : {}),
      ...(maxTokens ? { max_tokens: parseInt(maxTokens, 10) } : {}),
      ...(model ? { model } : {}),
    },
  });

  const validate = (): boolean => {
    if (!name.trim()) {
      toast({ title: "Validation", description: "Name is required.", variant: "destructive" });
      return false;
    }
    if (!content.trim()) {
      toast({ title: "Validation", description: "Prompt content is required.", variant: "destructive" });
      return false;
    }
    return true;
  };

  const handleSave = async (andActivate = false) => {
    if (!validate()) return;

    try {
      setSaving(true);
      const formData = buildFormData();

      let savedTemplate: AiPromptTemplate;
      if (isEditing) {
        savedTemplate = await updateAiPromptTemplate(Number(id), formData);
      } else {
        savedTemplate = await createAiPromptTemplate(formData);
      }

      if (andActivate) {
        await activateAiPromptTemplate(savedTemplate.id);
        toast({ title: "Saved & Activated", description: `"${savedTemplate.name}" is now active.` });
      } else {
        toast({ title: "Saved", description: `"${savedTemplate.name}" saved as draft.` });
      }

      navigate("/admin/ai/prompt-templates");
    } catch (error) {
      console.error("Error saving template:", error);
      toast({ title: "Error", description: "Failed to save template.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditing ? "Edit Prompt Template" : "New Prompt Template"}>
        <div className="flex h-64 w-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditing ? "Edit Prompt Template" : "New Prompt Template"}>
      <div className="space-y-6 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/ai/prompt-templates")}
          className="mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Templates
        </Button>

        {isEditing && template?.isActive && (
          <Alert>
            <AlertDescription>
              This template is currently <strong>active</strong>. Changes will take effect immediately.
            </AlertDescription>
          </Alert>
        )}

        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Template Details
              {isEditing && template && (
                <Badge variant={template.isActive ? "default" : "secondary"} className="ml-2">
                  {template.isActive ? "Active" : "Draft"} &middot; v{template.version}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Book Chat System Prompt v2"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="purpose">Purpose *</Label>
                <Select
                  value={purpose}
                  onValueChange={(v) => setPurpose(v as AiPromptPurpose)}
                  disabled={isEditing}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AI_PROMPT_PURPOSES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {AI_PROMPT_PURPOSES.find((p) => p.value === purpose)?.description}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="persona">Persona (Optional)</Label>
                <Select value={personaId} onValueChange={setPersonaId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a persona..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {personas.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Link this template to a specific AI persona.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What does this template do?"
                  rows={2}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prompt Content */}
        <Card>
          <CardHeader>
            <CardTitle>Prompt Content</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter the full prompt text..."
                rows={20}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                {content.length} characters
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>Model Configuration (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="model">Model Name</Label>
                <Input
                  id="model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="e.g. gemini-3-flash-preview"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="temperature">Temperature</Label>
                <Input
                  id="temperature"
                  type="number"
                  step="0.1"
                  min="0"
                  max="2"
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="e.g. 0.3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxTokens">Max Tokens</Label>
                <Input
                  id="maxTokens"
                  type="number"
                  min="1"
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(e.target.value)}
                  placeholder="e.g. 1536"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button onClick={() => handleSave(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save as Draft"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSave(true)}
            disabled={saving}
          >
            <Zap className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save & Activate"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/ai/prompt-templates")}
          >
            Cancel
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AiPromptTemplateForm;
